import { observer } from "mobx-react-lite";
import React, { useState } from "react";
import { Flex, Spacer, Text, chakra, Center, useMediaQuery, Box } from "@chakra-ui/react";
import Trigger from "rc-trigger";

import SubPanelWithScrollBar from "@/components/SubPanel/WithScrollBar";

import { tasksStore, TaskType } from "@/stores/tasks";
import { accelerationStore } from "@/stores/acceleration";

import { useTranslate } from "@/utils/lang";
import { Editor_Words } from "@/utils/lang/editor";
import { formatDuration } from "@/utils/format-time";

import Icon from "@/components/Icon";
import { ReactComponent as ClockSvg } from "@/assets/icons/clock.svg";
import { ReactComponent as ArrowSvg } from "@/assets/icons/arrow-down.svg";
import { genBlock } from "@/utils/bem";

import "./task-panel.scss";
import { cancelAllTask } from "@/utils/batchTask";
import { toast } from "@/components/Toast";
import { loginStore } from "@/modules/login/store";
import { isVip } from "@/utils/status";
import { EllipseText } from "../../components/styleComponents/Texts";

const Task = observer<{
  task: TaskType;
  color: string;
}>(({ task: { type, status, pending, isKafka, batchId }, color }) => {
  const { translate } = useTranslate();
  const working = isKafka ? status === "working" : pending === 0;

  if (isKafka && !batchId) {
    pending = Math.round(pending * accelerationStore.pendingFactor);
  }

  const waitTime = Math.ceil(pending / 3) * 30;
  return (
    <Flex
      w="100%"
      h="48px"
      pl="24px"
      pr="16px"
      color={color}
      fontSize="12px"
      userSelect="none"
      align="center"
      flexShrink={0}
    >
      <Text fontSize="14px" color={working ? "#3fc9a8" : color}>
        {translate(`creator-${type}-task`)}{" "}
        {working ? translate(Editor_Words["creator-working-message"]) : ""}
      </Text>
      <Spacer />
      {working ? null : (
        <Text mr="8px" whiteSpace="nowrap" display="inline-flex" alignItems="baseline">
          {translate("pending-position")} {pending}
          {waitTime ? (
            <chakra.span
              ml="5px"
              fontSize="16px"
              w="68px"
              position="relative"
              top="-2px"
              transformOrigin="left bottom"
              whiteSpace="nowrap"
              transform="scale(0.625)"
              display="inline-block"
            >
              {translate("pending-time")}&nbsp;{formatDuration(waitTime)}
            </chakra.span>
          ) : null}
        </Text>
      )}
    </Flex>
  );
});

const block = genBlock("c-task-panel");
const _TaskPanel: React.FC = () => {
  const [tasksVisible, setTasksVisible] = useState(false);
  const innerSize = "32px";
  const { translate } = useTranslate();
  const numTasks = tasksStore.tasks.size;

  function handleCancelAllTask() {
    cancelAllTask()
      .then(() => {
        toast({
          status: "success",
          title: translate(Editor_Words["cancel_tasks_success"]),
          position: "top",
        });
      })
      .catch(() => {
        toast({
          status: "error",
          title: translate(Editor_Words["cancel_tasks_failed"]),
          position: "top",
        });
      });
  }

  const [isSmallThan1200] = useMediaQuery("(max-width: 1200px)");

  return (
    <Box>
      <Trigger
        action={["click"]}
        popupVisible={tasksVisible}
        onPopupVisibleChange={setTasksVisible}
        popupStyle={{
          boxShadow: "1px 0px 4px 2px rgba(0, 0, 0, 0.25)",
        }}
        popup={
          <Flex flexDirection="column" bg="bg.panelBg" boxShadow="0px 0px 20px rgb(0 0 0 / 25%)">
            {isSmallThan1200 ? (
              <EllipseText
                display="inline-flex"
                alignItems="center"
                fontSize="14px"
                pt="12px"
                color="text.caption"
                fontWeight={600}
                justifyContent="center"
                userSelect="none"
              >
                {translate(Editor_Words["num-tasks-message-prefix"])}
                {` ${numTasks} `}
                {translate(Editor_Words["num-tasks-message-postfix"])}
              </EllipseText>
            ) : (
              ""
            )}
            <SubPanelWithScrollBar w="300px" h="400px" py="10px" bg="bg.panelBg">
              {tasksStore.tasks
                .filter(({ status }) => status !== "finished" && status !== "exception")
                .map((task) => (
                  <Task key={task.key} task={task} color="text.caption" />
                ))}
            </SubPanelWithScrollBar>

            {isVip() && tasksStore.batchs?.length > 0 ? (
              <Center
                w="100%"
                bg="bg.panelBg"
                borderTop="1px solid #3D3D3D"
                color="brand.primary"
                cursor="pointer"
                fontSize="14px"
                lineHeight="22px"
                py="7px"
                onClick={handleCancelAllTask}
              >
                {translate(Editor_Words["cancel_all_nolibox_tasks"])}
              </Center>
            ) : null}
          </Flex>
        }
        popupAlign={{
          points: ["tc", "bc"],
          offset: [-44, 12],
        }}
      >
        <Flex pr="18px" color="text.icon" cursor="pointer" className={block()} maxW="100%">
          <Center w={innerSize}>
            <Icon squared={false} className={block({ e: "clock" })} svg={ClockSvg} />
          </Center>
          {isSmallThan1200 ? (
            ""
          ) : (
            <EllipseText
              display="inline-flex"
              alignItems="center"
              fontSize="14px"
              userSelect="none"
            >
              {translate(Editor_Words["num-tasks-message-prefix"])}
              {` ${numTasks} `}
              {translate(Editor_Words["num-tasks-message-postfix"])}
            </EllipseText>
          )}
          <Center w={innerSize}>
            <Icon squared={false} className={block({ e: "arrow" })} svg={ArrowSvg} />
          </Center>
        </Flex>
      </Trigger>
    </Box>
  );
};

export const TaskPanel = observer(_TaskPanel);
